import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Flex, Grid, Segmented, Typography } from 'antd'
import { HistoryOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { OperationDetail } from '@/features/profile/OperationDetail'
import { OperationsFilter } from '@/features/profile/OperationsFilter'
import { OperationsList } from '@/features/profile/OperationsList'
import { SummaryCards } from '@/features/profile/SummaryCards'
import {
  EMPTY_FILTER,
  isFilterActive,
  moneyOnlyApplies,
  type AgentOperation,
  type OperationFilter,
} from '@/features/profile/profileApi'
import { quickRange, tenantRangeWindow, type DayRange } from '@/shared/lib/tenantDay'
import {
  useAgentOperations,
  useCommissionSummary,
  useMyAccount,
} from '@/features/profile/useMyAccount'
import { EmptyState } from '@/shared/ui/EmptyState'

/**
 * KOMPYUTERDAGI kenglik chegarasi.
 *
 * ⚠️ 720 dan kengaytirildi (egasi talabi, 2026-08-20). O'lchangan
 * oqibat: `Card size="small"` ichida foydali kenglik ≈ 696px edi, besh
 * ustunning qotirilgan kengliklari 590px ni olardi va abonent ustuniga
 * 106px qolardi — ya'ni TELEFONDAGI kartochkadan torroq. Shu vaqtda
 * 1920px li monitorda o'ng tomonda 1200px bo'sh turardi.
 *
 * ⚠️ TELEFONGA YETIB BORMAYDI: tor ekranda `isMobile` tarmog'i butunlay
 * boshqa kod (kartochka ro'yxati) va oyna kengligi 390–430px, ya'ni bu
 * son u yerda hech narsani cheklamaydi. Shunday bo'lsa ham chegara
 * OSHKOR ravishda faqat kompyuterga qo'yiladi — kelajakda son
 * kichraytirilsa ham telefon tegilmasin.
 */
const DESKTOP_MAX_WIDTH = 1160

const DEPOSIT_FILTER: OperationFilter = { kinds: ['DEPOSIT'], status: null, q: '' }

/**
 * «Mening kassa amallarim» — 11-SHERIK-KABINETI-UI.md §5.
 *
 * Kassirning savoli bitta: «bugun nima qildim, qancha pul oldim».
 * Ekran shu savolga ikki qavatda javob beradi:
 *
 *   1. YIG'INDI — to'ldirishlar soni, o'tkazilgan summa, komissiya.
 *      Manba `GET /api/agents/{id}/commission-summary`.
 *   2. RO'YXAT — vaqt · abonent · tur · summa · natija. Manba
 *      `GET /api/agents/{id}/operations`.
 *
 * ⚠️ Ro'yxatning manbai IKKITA: ledger ko'chirmasi (summa, `txn`) va
 * audit jurnali (abonent kim, urinish nega o'tmadi). Sabab tuzilmaviy —
 * ko'chirmada qarshi tomon yo'q, rad etilgan urinish esa jurnalga
 * umuman tushmaydi. Shuning uchun ba'zi qatorlarda `login` yoki `txnId`
 * bo'sh bo'lishi MUMKIN va bu nosozlik emas.
 *
 * ⚠️ MAVJUD TO'LOVLAR RO'YXATI BU YERGA YARAMAYDI va ADR-030 §5 buni
 * oldindan yozgan: u «o'zi sotgan abonentlar» bo'yicha filtrlanadi
 * (`PaymentScopeResolver`), yangi modelda esa agentda O'Z abonenti
 * yo'q — ro'yxat har doim BO'SH chiqardi va bo'shligi «bugun ishlamadim»
 * bo'lib o'qilardi.
 *
 * ⚠️ SANA TENANT ZONASIDA hisoblanadi (`tenantRangeWindow`). `dayjs()`
 * brauzernikini olardi va Kobul tenantida kuniga yarim soat davomida
 * yig'indi bir kunga surilib ketardi — JIMGINA, chunki so'rov baribir
 * 200 qaytaradi. Oraliq uchun ham AYNAN shu almashtirish.
 *
 * ⚠️ DEPOZITLAR ALOHIDA EMAS, FILTR (egasi qarori, 2026-08-19).
 * «Depozitlar» tugmasi `kind=DEPOSIT` filtrini qo'yadi va ustunlarni
 * almashtiradi (sana · summa · kim · havola). Ikkinchi sahifa ochish
 * ikkinchi endpoint, ikkinchi kesh va ikkinchi bo'sh holat degani
 * bo'lardi — javob esa AYNI ro'yxatda allaqachon bor.
 *
 * <h2>⚠️ SUKUT — FAQAT PUL AMALLARI (egasi talabi, 2026-08-21)</h2>
 * «amallarimda faqat pul bilan bog'liq amallar bo'lsin va paketlar
 * alohida menyuda bo'lsin». Torayish SERVERDA bo'ladi
 * (`moneyOnlyApplies` -> so'rov parametri): qatorlarni kelgandan keyin
 * elash faqat JORIY sahifani toraytirardi, ro'yxat esa serverda
 * sahifalanadi.
 *
 * ⚠️ TORAYISH KO'RINIB TURADI va bir bosishda ochiladi. Aks holda
 * kassir paket berishlarini «yozilmagan» deb o'ylardi — ma'lumotni
 * indamay yashirish esa bu panelda taqiqlangan. Ochilgach ro'yxat AYNI
 * so'rovni bayroqsiz yuboradi, ya'ni paketlar pul amallari orasida
 * qaytadi.
 */
export function MyOperationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const screens = Grid.useBreakpoint()
  const account = useMyAccount()

  const [range, setRange] = useState<DayRange>(() => quickRange('today'))
  const [filter, setFilter] = useState<OperationFilter>(EMPTY_FILTER)
  /** ⚠️ Sukut `true` — egasi talabi. Foydalanuvchi uni O'ZI ocha oladi. */
  const [moneyOnly, setMoneyOnly] = useState(true)
  const window = useMemo(() => tenantRangeWindow(range), [range])
  const summary = useCommissionSummary(account.agentId, window)
  const [page, setPage] = useState(0)

  /**
   * ⚠️ Tanlangan qator NUSXA bo'lib saqlanadi, indeks emas: «Yana
   * yuklash» ro'yxatni almashtiradi va indeks o'sha paytda BOSHQA amalni
   * ko'rsata boshlardi — jimgina, chunki oyna baribir to'la ko'rinardi.
   */
  const [selected, setSelected] = useState<AgentOperation | null>(null)

  // Davr yoki filtr o'zgarganda sahifa NOLGA qaytadi va ochiq oyna
  // yopiladi: aks holda kassir 3-sahifada turib boshqa davrni tanlasa
  // bo'sh ro'yxat ko'rardi, oynada esa boshqa davrning amali qolardi.
  useEffect(() => {
    setPage(0)
    setSelected(null)
  }, [range, filter, moneyOnly])

  const operations = useAgentOperations(account.agentId, window, filter, page, moneyOnly)

  // «Tozalash» SUKUT KO'RINISHGA qaytaradi — tanlangan turlar ham,
  // ochilgan paketlar ham. Yarim tozalash «tozalandi» deb ko'rinib,
  // ro'yxatni boshqa holatda qoldirardi.
  const resetFilter = useCallback(() => {
    setFilter(EMPTY_FILTER)
    setMoneyOnly(true)
  }, [])

  const rows = operations.page?.rows ?? []
  const isMobile = screens.md === false
  const filtered = isFilterActive(filter)
  // Depozit ko'rinishi TANLOVDAN kelib chiqadi, alohida holat emas —
  // ikki manba bir-biridan ajralib ketmasin.
  const depositsOnly = filter.kinds.length === 1 && filter.kinds[0] === 'DEPOSIT'
  /**
   * ⚠️ IKKALASI HAM AYNI QOIDADAN, ta'rif TAKRORLANMAYDI: aniq tur
   * tanlanganda bayroq so'rovga umuman qo'shilmaydi, ya'ni «faqat pul
   * amallari» deyish YOLG'ON da'vo bo'lardi — belgi o'shanda umuman
   * chizilmaydi.
   */
  const canNarrow = moneyOnlyApplies(filter, true)
  const narrowed = moneyOnlyApplies(filter, moneyOnly)

  return (
    <div style={{ maxWidth: isMobile ? undefined : DESKTOP_MAX_WIDTH, margin: '0 auto' }}>
      {/* Sarlavha faqat kompyuterda — telefonda uni kabinetning yuqori
          qatori ko'rsatadi (`CashdeskPage` bilan bir xil qaror). */}
      {isMobile ? null : (
        <div style={{ marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('myOperations.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('myOperations.subtitle')}</Typography.Text>
        </div>
      )}

      <Flex vertical gap={16}>
        {account.agentId === null ? (
          <EmptyState
            icon={<HistoryOutlined />}
            title={t('profile.noNodeTitle')}
            description={t('profile.noNodeText')}
          />
        ) : (
          <>
            <Segmented<'all' | 'deposits'>
              block={isMobile}
              value={depositsOnly ? 'deposits' : 'all'}
              onChange={(value) => setFilter(value === 'deposits' ? DEPOSIT_FILTER : EMPTY_FILTER)}
              options={[
                {
                  value: 'all',
                  // Yorliq HOLATNI aytadi: torayish yoqilganda «Hamma
                  // amallar» deb yozish paket berishlarni ham va'da
                  // qilardi.
                  label: moneyOnly ? t('myOperations.view.money') : t('myOperations.view.all'),
                },
                { value: 'deposits', label: t('myOperations.view.deposits') },
              ]}
            />

            <OperationsFilter
              range={range}
              onRangeChange={setRange}
              filter={filter}
              onFilterChange={setFilter}
              onReset={resetFilter}
            />

            <SummaryCards state={summary} awaitingNote={t('myOperations.summaryAwaiting')} />

            {/* ⚠️ Sanoq (`SummaryCards`) BU RO'YXATDAN emas,
                `commission-summary` dan keladi va u FAQAT to'ldirish
                yozuvlarini sanaydi — ya'ni paket berishlar unga
                hech qachon kirmagan. Shu sababli belgi ostidagi son
                bilan ro'yxat bir-biriga zid bo'lmaydi. */}
            {canNarrow ? (
              <Alert
                type="info"
                showIcon
                message={
                  narrowed ? t('myOperations.moneyOnly.on') : t('myOperations.moneyOnly.off')
                }
                action={
                  <Button size="small" type="link" onClick={() => setMoneyOnly((prev) => !prev)}>
                    {narrowed
                      ? t('myOperations.moneyOnly.showAll')
                      : t('myOperations.moneyOnly.showMoney')}
                  </Button>
                }
              />
            ) : null}

            <Card
              size="small"
              title={depositsOnly ? t('myOperations.depositsTitle') : t('myOperations.listTitle')}
            >
              <Flex vertical gap={12}>
                <OperationsList
                  state={operations}
                  onLoadMore={() => setPage((p) => p + 1)}
                  onSelect={setSelected}
                  variant={depositsOnly ? 'deposits' : 'all'}
                  filtered={filtered}
                  onResetFilter={resetFilter}
                />
                {/* Bo'sh holat boshi berk ko'cha bo'lmasin — kassir
                    shu yerdan ishiga qaytadi (§5). */}
                <Button type="primary" onClick={() => void navigate('/')}>
                  {t('myOperations.toCashdesk')}
                </Button>
              </Flex>
            </Card>
          </>
        )}
      </Flex>

      {/* ⚠️ Cashback SHU SAHIFADAGI qatorlar orasidan topiladi: komissiya
          alohida tranzaksiya va ro'yxatda o'z qatori bo'lib turadi
          (`cashbackOf`). Alohida so'rov uchun endpoint yo'q. */}
      <OperationDetail row={selected} rows={rows} onClose={() => setSelected(null)} />
    </div>
  )
}
